import type { FlowNode, FlowEdge } from '../types';

export interface NodeIssue {
  level: 'warn' | 'error';
  message: string;
}

/**
 * Per-node validation. Returns a map of node id → list of issues.
 *
 * Issues:
 *  - Trigger with no outgoing edges → "this trigger leads nowhere"
 *  - Any non-trigger node with no incoming edges → "disconnected"
 *  - Action node missing body → "no message body"
 *  - Action node missing to → "no recipient"
 *  - Condition node missing field/value → "incomplete"
 *  - AI smart node missing prompt → "no prompt"
 */
export function validateNodes(nodes: FlowNode[], edges: FlowEdge[]): Map<string, NodeIssue[]> {
  const issues = new Map<string, NodeIssue[]>();
  const push = (id: string, level: NodeIssue['level'], message: string) => {
    const cur = issues.get(id) || [];
    cur.push({ level, message });
    issues.set(id, cur);
  };

  for (const n of nodes) {
    const d = n.data;
    const incoming = edges.filter(e => e.target === n.id).length;
    const outgoing = edges.filter(e => e.source === n.id).length;

    if (d.kind === 'trigger' && outgoing === 0) {
      push(n.id, 'warn', 'Trigger has no next step');
    }
    if (d.kind !== 'trigger' && incoming === 0) {
      push(n.id, 'warn', 'Disconnected · nothing reaches this block');
    }
    if (d.kind === 'action') {
      if (d.type === 'sms.send' || d.type === 'email.send') {
        if (!d.body || !d.body.trim()) push(n.id, 'warn', 'No message body');
        if (!d.to || !d.to.trim()) push(n.id, 'warn', 'No recipient');
      }
      if (d.type === 'webhook.post' && (!d.to || !/^https?:\/\//.test(d.to))) {
        push(n.id, 'warn', 'Webhook URL is missing or invalid');
      }
    }
    if (d.kind === 'condition') {
      if (!d.field || !d.operator || d.value === undefined || d.value === null || d.value === '') {
        push(n.id, 'warn', 'Condition is incomplete');
      }
      if (outgoing === 0) {
        push(n.id, 'warn', 'Condition has no branch wired');
      }
    }
    if (d.kind === 'ai_smart') {
      if (!d.prompt || !d.prompt.trim()) push(n.id, 'warn', 'AI prompt is empty');
      if (!d.model) push(n.id, 'warn', 'No model selected');
    }
    if (d.kind === 'wait') {
      if (typeof d.amount !== 'number' || d.amount <= 0) push(n.id, 'warn', 'Wait duration must be positive');
    }
  }
  return issues;
}
